/**
 * smartViews — typed smart-view ids + filter logic for the Documents hub.
 *
 * The 5 smart views (Tutti / Ricevuti / Drive / Ando AI / Pinnati) sit
 * on top of the raw ``WorkspaceDocument`` feed. Each view is a pure
 * predicate so we can both render the active list AND compute per-chip
 * badge counts from the same source of truth.
 *
 * URL contract: ``?view=tutti|ricevuti|drive|ando|pinnati`` (default
 * ``tutti``). Unknown values fall back to ``tutti`` rather than
 * throwing — keeps deep-links resilient to future renames.
 */

import type { WorkspaceDocument } from "@ando/core";

/** Canonical smart-view id union — narrow string literals. */
export type SmartView = "tutti" | "ricevuti" | "drive" | "ando" | "pinnati";

export const SMART_VIEWS: readonly SmartView[] = [
  "tutti",
  "ricevuti",
  "drive",
  "ando",
  "pinnati",
];

const SMART_VIEW_SET = new Set<string>(SMART_VIEWS);

/** Coerce a raw URL value to a typed ``SmartView``. */
export function parseSmartView(raw: unknown): SmartView {
  if (typeof raw === "string" && SMART_VIEW_SET.has(raw)) {
    return raw as SmartView;
  }
  return "tutti";
}

/**
 * Soft "pinned" flag — the BE wire type does not carry it yet, so we
 * read off the optional structural field. When the BE lands the column,
 * just add it to ``WorkspaceDocument`` and this helper keeps working
 * without changes.
 */
export function isPinned(doc: WorkspaceDocument): boolean {
  const candidate = (doc as unknown as { pinned?: unknown }).pinned;
  return candidate === true;
}

/**
 * "Ricevuti" cutoff — within 48h. Slightly wider than the 24h "today"
 * bucket so the chip stays useful across a weekend (Mon morning still
 * shows Fri evening attachments).
 */
const RICEVUTI_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Predicate per smart view. The same function is used for ``filter``
 * (active list) AND for badge counting (filter+length).
 */
export function matchesSmartView(
  doc: WorkspaceDocument,
  view: SmartView,
  now: Date = new Date(),
): boolean {
  switch (view) {
    case "tutti":
      return true;
    case "ricevuti": {
      if (
        doc.source_type !== "chat" &&
        doc.source_type !== "email_attachment" &&
        doc.source_type !== "manual_upload"
      ) {
        return false;
      }
      const ts = doc.updated_at ?? doc.created_at;
      if (!ts) return false;
      const t = Date.parse(ts);
      if (Number.isNaN(t)) return false;
      return now.getTime() - t <= RICEVUTI_WINDOW_MS;
    }
    case "drive":
      return doc.source_type === "drive";
    case "ando":
      return doc.source_type === "ai_generated";
    case "pinnati":
      return isPinned(doc);
    default:
      return true;
  }
}

/** Apply a smart view to a doc list. Pure. */
export function filterBySmartView(
  docs: readonly WorkspaceDocument[],
  view: SmartView,
  now: Date = new Date(),
): readonly WorkspaceDocument[] {
  if (view === "tutti") return docs;
  return docs.filter((d) => matchesSmartView(d, view, now));
}
